/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";

// Component imports
import { Button, Col, Grid, Row, Space, Table, Typography } from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { CreateProjectModal } from "@/components/ProjectsComponents/CreateProjectModal";
import { Container } from "@/components/UI/Container";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";

// misc
import { Trans, useTranslation } from "react-i18next";
import { useGetProjects } from "@/api/projects";
import replaceUrlParams from "@/utils/string/replaceUrlParams";
import { NAV_PROJECTS, NAV_PROJECT_SINGLE_VIEW, NAV_SUBSCRIPTION_PLANS, PAGINATION_OPTIONS, RAG_DOCUMENTATION_URL, SUPPORT_EMAIL } from "@/constants";
import { IProject } from "@/interfaces/Project.interface";
import { useWorkspacesList } from "@/api/workspaces";
import { queryClient } from "@/utils/queryClient";
import { ITokens } from "@/interfaces/User.interface";

const { useBreakpoint } = Grid;
const { Text } = Typography;
const baseClassName = "projects-screen";
/**
 * Component to display the projects list and handle the creation of a new one
 * @returns JSX.Element
 */
export const Projects = (): JSX.Element => {
  const screens = useBreakpoint();
  const isScreenBelow = (size: "sm" | "md" | "lg" | "xl" | "xxl") => screens.xs || (screens.sm && !screens[size]);
  const { t } = useTranslation();
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);

  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);
  const { access: accessToken } = userAuth || {};
  const { data: workspacesList } = useWorkspacesList({
    enabled: !!accessToken,
  });

  const { data: projects, isLoading: isFetchingProjects } = useGetProjects(workspaceId as string);

  if (workspacesList && !workspaceId) {
    return(
      <Navigate
        to={replaceUrlParams(NAV_PROJECTS, {
          workspaceId: workspacesList[0]?.uuid,
        })}
        replace
      />
    )
  }

  if (isFetchingProjects && !projects) {
    // Show skeleton
    return <DataLoadingSkeleton />;
  }

  // No projects created yet
  if (!projects || !projects?.length) {
    return (
      <Container className={`${baseClassName} no-projects`}>
        <Row justify="center" align="middle">
          <Col lg={14} md={16} xs={24} className="text-center">
            <h1  className={`${baseClassName}__title`}>
              {t("projects.no_project_title")}
            </h1>
            <h3 className={`${baseClassName}__subtitle`}>
              {t("projects.subtitle")}
            </h3>
            <p className={`${baseClassName}__description`}>
              <Trans parent="p" i18nKey="projects.no_projects">
                Welcome to RAG-Buddy! Get started by creating a project that suits your use case. Either as a Text Classification system or a RAG
                pipeline. Please make sure to consult the
                <a href={RAG_DOCUMENTATION_URL} target="_blank" rel="noopener noreferrer">
                  documentation
                </a> and don't hesitate to <a href={`mailto:${SUPPORT_EMAIL}`}>contact us</a> when you need help.
              </Trans>
            </p>
            {/* Create Project Button */}
            <div className={`${baseClassName}__new-project-wrapper`}>
              <Button onClick={() => setModalVisible(true)} disabled={modalVisible} type="primary" style={{ margin: "auto" }} icon={<PlusOutlined />}>
                {t("projects.create_first_project")}
              </Button>
            </div>
            {/* ./ Create Project Button */}
          </Col>
        </Row>
        {/* Create Project Modal */}
        <CreateProjectModal isModalOpen={modalVisible} onClose={() => setModalVisible(false)} />
        {/* ./ Create Project Modal */}
      </Container>
    );
  }

  // Project columns
  const columns = [
    {
      title: t("projects.list.project_title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: t("projects.list.subscription_plan"),
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      render: (plan:string, record: IProject) => (
        <>
          <span>{plan} </span>
          {
            record.is_trial
            ? <Text type="success">({t("project_single.in_trial")})</Text>
            : null
          }
        </>
      )
    },
    {
      title: t("projects.list.services"),
      key: "action",
      width: isScreenBelow("md") ? 50 : 320,
      render: (_: any, record: IProject) => {
        const params = {
          workspaceId: workspaceId as string,
          projectId: record.uuid as string,
        };
        return (
          <Space size={0}>
            {/* Link to Project's Cache Settings */}
            <Button
              className={`${screens.md ? "d-none" : ""} `}
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(
                  `${replaceUrlParams(NAV_PROJECT_SINGLE_VIEW, {
                    ...params,
                  })}?tab=services`, { state: { canGoBack: true } }
                )
              }
            />

            <Button
              className={`${isScreenBelow("md") ? "d-none" : ""} pl-0`}
              onClick={() =>
                navigate(
                  `${replaceUrlParams(NAV_PROJECT_SINGLE_VIEW, {
                    ...params,
                  })}?tab=services`, { state: { canGoBack: true } }
                )
              }
              type="link"
            >
              {t("projects.services")}
            </Button>
            {/* ./ Link to Project's Cache Settings */}
            {/* Link to Project's Analytics */}
            <Button
              className={`${isScreenBelow("md") ? "d-none" : ""}`}
              onClick={() =>
                navigate(
                  `${replaceUrlParams(NAV_PROJECT_SINGLE_VIEW, {
                    ...params,
                  })}?tab=analytics`, { state: { canGoBack: true } }
                )
              }
              type="link"
            >
              {t("projects.analytics")}
            </Button>
            {/* ./ Link to Project's Analytics */}
            {/* Link to Project's Subscription */}
            <Button
              className={`${isScreenBelow("md") ? "d-none" : ""} pr-0`}
              onClick={() =>
                navigate(
                  `${replaceUrlParams(NAV_SUBSCRIPTION_PLANS, {
                    ...params,
                  })}`, { state: { canGoBack: true } }
                )
              }
              type="link"
            >
              {t("projects.subscription")}
            </Button>
            {/* ./ Link to Project's Subscription */}
          </Space>
        );
      },
    },
  ];

  return (
    <Container className={baseClassName}>
      <Row justify="center">
        <Col xs={24}>
          {/* Create Project Button */}
          <Button className="float-right mb-3" onClick={() => setModalVisible(true)} disabled={modalVisible} icon={<PlusOutlined />} type="primary">
            {t("create_project.title")}
          </Button>
          {/* ./ Create Project Button */}
          {/* Projects List */}
          <Table
            rowKey="uuid"
            size="small"
            dataSource={projects}
            columns={columns}
            scroll={{ x: "overflow" }}
            bordered
            pagination={{ ...PAGINATION_OPTIONS }}
          />
          {/* ./ Projects List */}
        </Col>
      </Row>
      {/* Create Project Modal */}
      <CreateProjectModal isModalOpen={modalVisible} onClose={() => setModalVisible(false)} />
      {/* ./ Create Project Modal */}
    </Container>
  );
};
